import * as React from "react";
import { useParams } from "react-router";
import Loader from "../../components/atoms/Loader";
import { DrillProgressLegend } from "../../components/features/DrillProgressLegend";
// WEB-1742: 仕様明確化まで UI / フィルタを一時停止。
// 復活時はこのブロックと下の生徒フィルタ呼び出し・StudentFilter 描画・StudentAnswerTable props をアンコメントするだけで戻る。
// 詳細: https://www.notion.so/3675fd6b740381fe90b1f33c8ccf5651
// import StudentFilter from "../../components/features/NewStudentFilter";
// import { useStudentFilterContext } from "../../components/features/NewStudentFilter/useStudentFilterContext";
import { SectionAnalyticsTypeTab } from "../../components/features/SectionAnalyticsTypeTab";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbItemLink,
} from "../../components/general/Breadcrumb/Breadcrumb";
// WEB-1742: 仕様明確化まで一時停止
// import { buildStudentFilterParams } from "../../helpers/FiltersHelper";
import { OutletContextProps } from "../../helpers/RouterHelper";
import enhanceSectionsSettingsPage, {
  SectionsSettingsPageProps,
} from "../../hocs/enhanceSectionsSettingsPage";
import { useQueryError } from "../../hooks/http/useQueryError";
import SectionInterface from "../../interfaces/SectionInterface";
import { useFetchDrillDetail } from "../SectionsAnalyticsRetentionLevelDetailPage/useFetchDrill";
import { ContentPanel } from "./ContentItemDetail";
import { StudentAnswerTable } from "./StudentAnswerTable";
import { useFetchDrillSectionContents } from "./useFetchDrillSectionContents";
import { useFetchStudentsWithAnswers } from "./useFetchStudentsWithAnswers";

export type UseSectionsAnalyticsDrillSectionDetailProps = {
  section: SectionInterface;
};

type Props = SectionsSettingsPageProps &
  UseSectionsAnalyticsDrillSectionDetailProps &
  OutletContextProps;

export const SectionsAnalyticsDrillSectionDetail = (props: Props) => {
  React.useEffect(() => {
    props.setActiveMenu("analytics");
  }, []);

  const { materialCode, drillSectionPublicId } = useParams<{
    materialCode: string;
    drillSectionPublicId: string;
  }>();
  const section = props.section;

  // WEB-1742: 仕様明確化まで一時停止
  // const { findTagFilter } = useStudentFilterContext();
  // const studentFilter = findTagFilter({ sectionId: section.id });
  // const studentFilterParams = studentFilter
  //   ? buildStudentFilterParams(studentFilter)
  //   : {};

  const {
    data: drill,
    isLoading: isDrillLoading,
    error: drillError,
  } = useFetchDrillDetail({
    section,
    materialCode,
  });

  useQueryError(drillError);

  const {
    data: contentsData,
    isLoading: isContentsLoading,
    error: contentsError,
  } = useFetchDrillSectionContents({
    sectionId: section.id,
    materialCode,
    drillSectionPublicId,
    // WEB-1955 (FILT-03): 3 階層目は連携中 (active) のみを表示・集計対象とする
    studentFilterParams: { status: ["active"] },
  });

  useQueryError(contentsError);

  const [selectedContentId, setSelectedContentId] = React.useState<
    string | undefined
  >(undefined);
  const [selectedItemPublicId, setSelectedItemPublicId] = React.useState<
    string | undefined
  >(undefined);

  // WEB-1742: 仕様明確化まで「未回答を非表示」を一時停止
  // 「未回答を非表示」の状態は親で保持し、コンテンツ切替の再フェッチ中も値を維持する
  // const [hideUnanswered, setHideUnanswered] = React.useState(false);

  // コンテンツデータ取得時に最初のコンテンツを選択（選択中のコンテンツが新レスポンスに存在する場合は保持）
  React.useEffect(() => {
    if (!contentsData?.contents || contentsData.contents.length === 0) {
      return;
    }
    if (
      !selectedContentId ||
      !contentsData.contents.some((c) => c.id === selectedContentId)
    ) {
      setSelectedContentId(contentsData.contents[0].id);
    }
  }, [contentsData, selectedContentId]);

  const {
    data: studentsData,
    isLoading: isStudentsLoading,
    error: studentsError,
  } = useFetchStudentsWithAnswers({
    sectionId: section.id,
    materialCode,
    drillContentPublicId: selectedContentId,
    // WEB-1955 (FILT-03): 3 階層目は連携中 (active) のみを表示・集計対象とする
    studentFilterParams: { status: ["active"] },
  });

  useQueryError(studentsError);

  const selectedContent = contentsData?.contents.find(
    (c) => c.id === selectedContentId,
  );

  // 選択中コンテンツのアイテム集合に selectedItemPublicId が含まれない場合は先頭アイテムを選ぶ
  React.useEffect(() => {
    if (!selectedContent || selectedContent.items.length === 0) {
      return;
    }
    if (
      !selectedItemPublicId ||
      !selectedContent.items.some((i) => i.publicId === selectedItemPublicId)
    ) {
      setSelectedItemPublicId(selectedContent.items[0].publicId);
    }
  }, [selectedContent, selectedItemPublicId]);

  if (isDrillLoading || !drill) {
    return <Loader loading />;
  }

  const drillSection = drill.sections.find(
    (s) => s.id === drillSectionPublicId,
  );
  const drillName = drill.name;
  const drillSectionName = drillSection?.name ?? "";
  // DISP-03: セクション別画面はセクション単位の allowStudiedAnswer で出し分ける。
  // セクションが見つからない場合は false（完了不可）として扱う。
  const sectionAllowStudiedAnswer = drillSection?.allowStudiedAnswer ?? false;

  const selectedItem = selectedContent?.items.find(
    (i) => i.publicId === selectedItemPublicId,
  );

  return (
    <>
      <SectionAnalyticsTypeTab
        sectionId={section.id}
        analyticsType="retentionLevel"
      />
      <div className="mt-5">
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbItemLink
              href={`/sections/${section.id}/analytics/retention_level`}
            >
              <div className="mr-4 text-lg">学習定着</div>
            </BreadcrumbItemLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbItemLink
              href={`/sections/${section.id}/analytics/retention_level/${materialCode}`}
            >
              <span className="ml-4">{drillName}</span>
            </BreadcrumbItemLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrent>
            <span className="ml-4">{drillSectionName}</span>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* WEB-1742: 仕様明確化まで一時停止 */}
        {/* <StudentFilter sectionId={section.id} /> */}

        <div className="mt-7 flex flex-nowrap items-center justify-between">
          {selectedContent?.registeredStudentsCount != null && (
            <p className="text-sm font-bold leading-4 text-black">
              <span>登録済み：</span>
              <span className="font-[Lato,sans-serif]">
                {selectedContent.registeredStudentsCount}
              </span>
              <span>人</span>
            </p>
          )}
          <div className="ml-auto">
            <DrillProgressLegend
              allowStudiedAnswer={sectionAllowStudiedAnswer}
            />
          </div>
        </div>

        <div
          className="mt-4 grid items-start gap-6 overflow-hidden"
          style={{ gridTemplateColumns: "320px minmax(0, 1fr)" }}
        >
          {/* Left section: コンテンツパネル */}
          <div className="min-w-0">
            {isContentsLoading || !contentsData ? (
              <Loader loading />
            ) : (
              <ContentPanel
                contents={contentsData.contents}
                selectedContentId={selectedContentId ?? ""}
                onSelectContent={setSelectedContentId}
                selectedItemPublicId={selectedItemPublicId}
                onSelectItem={setSelectedItemPublicId}
                sectionName={drillSectionName}
                allowStudiedAnswer={sectionAllowStudiedAnswer}
              />
            )}
          </div>

          {/* Right section: 生徒別回答テーブル */}
          <div className="min-w-0 rounded-md border border-solid border-gray-400 bg-white p-4">
            {isStudentsLoading || !studentsData ? (
              <Loader loading />
            ) : (
              <StudentAnswerTable
                students={studentsData.studentsWithAnswers}
                selectedItem={selectedItem}
                allowStudiedAnswer={sectionAllowStudiedAnswer}
                // WEB-1742: 仕様明確化まで「未回答を非表示」props を一時停止
                // hideUnanswered={hideUnanswered}
                // onHideUnansweredChange={setHideUnanswered}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const pageInfo = {
  title: "アナリティクス",
};

export const SectionsAnalyticsDrillSectionDetailPage =
  enhanceSectionsSettingsPage(SectionsAnalyticsDrillSectionDetail, pageInfo);
